import {
  packageStatus,
  putPackagingSequenceAsSuperOwner,
} from "@actions/superOwnerApp/templatePackages/learningTemplatePackage";
import { ReduxStatus } from "@constants/redux";
import {
  LearningTemplatePackageBase,
  PackagingAsLearningTemplatePackageBase,
  PackagingPackageableType,
} from "@lib/Api";
import { SuperOwnerAppLearningTemplatePackagesState } from "@reducers/superOwnerApp/templatePackages/learningTemplatePackage";
import { useAppDispatch } from "@root/hooks";
import { reorder } from "@utils/reorder/reorder";
import { useEffect, useState } from "react";
import { DropResult } from "react-beautiful-dnd";

type Props = {
  learningTemplate: LearningTemplatePackageBase;
  packages: PackagingAsLearningTemplatePackageBase[];
  isSuccess: boolean;
  refetch: () => void;
  learningTemplateCache: SuperOwnerAppLearningTemplatePackagesState;
};

export const usePackageListView = ({
  learningTemplate,
  packages,
  isSuccess,
  refetch,
  learningTemplateCache,
}: Props) => {
  const {
    updated,
    success: postedPackage,
    deletedPackage,
    deleteSuccess,
  } = learningTemplateCache;

  const dispatch = useAppDispatch();
  const [isSorting, setIsSorting] = useState<boolean>(false);
  const [reorderPackageList, setReorderPackageList] = useState(
    isSuccess ? packages : [],
  );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }

    const newListSequence = reorder(
      reorderPackageList,
      result.source.index,
      result.destination.index,
    );

    setReorderPackageList(newListSequence);
  };

  const handleSaveSortedPackageList = () => {
    const promiseAll: Promise<ReturnType<typeof useAppDispatch>>[] = [];
    const updateRequests = reorderPackageList.map((item, idx: number) => {
      const packageableType = item?.material_homework_template_package
        ? PackagingPackageableType.Package
        : item.packageable_type;
      const packageableId =
        item?.material_homework_template_package?.id ?? item.packageable_id;

      return {
        query: {
          package_id: learningTemplate.id,
          packageable_type: packageableType,
          packageable_id: packageableId,
        },
        data: { sequence: idx },
      };
    });

    updateRequests.forEach((request) => {
      promiseAll.push(dispatch(putPackagingSequenceAsSuperOwner(request)));
    });
    Promise.allSettled(promiseAll).then((resolves) => {
      const successResolve: PromiseSettledResult<any> | undefined =
        resolves.find(
          (resolve: any) =>
            resolve.value.meta.requestStatus === ReduxStatus.fulfilled,
        );
      if (successResolve) {
        dispatch(
          packageStatus({
            message: "順番が更新されました。",
            status: ReduxStatus.fulfilled,
          }),
        );
      } else {
        const failedResolve: PromiseSettledResult<any> | undefined =
          resolves.find(
            (resolve: any) =>
              resolve.value.meta.requestStatus === ReduxStatus.rejected,
          );
        if (failedResolve) {
          dispatch(
            packageStatus({
              message: "順番が更新されませんでした。",
              status: ReduxStatus.rejected,
            }),
          );
        }
      }
    });
    setIsSorting(false);
  };

  const handleActivateSort = () => {
    if (!isSorting) {
      setIsSorting(true);
    } else {
      handleSaveSortedPackageList();
    }
  };

  const handleDeactivateSort = () => {
    setIsSorting(false);
  };

  useEffect(() => {
    setReorderPackageList(packages);
  }, [isSuccess, packages]);

  useEffect(() => {
    if (postedPackage || updated || (deletedPackage && deleteSuccess)) {
      refetch();
    }
  }, [postedPackage, updated, deletedPackage, deleteSuccess]);

  const isHasReorderPackageList = !!reorderPackageList.length;

  return {
    onDragEnd,
    reorderPackageList,
    isSorting,
    isHasReorderPackageList,
    handleActivateSort,
    handleDeactivateSort,
  };
};
